import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, Check, LoaderCircle, Terminal, X } from "lucide-react";
import { Navbar } from "../components/landing/Navbar";
import { Footer } from "../components/landing/Sections";
import { siteConfig } from "../data/mock";
import "../App.css";

const INITIAL_FORM = {
  name: "",
  email: "",
  profile: "",
  phone: "",
  role: "",
  hard_problems: "",
};

const INITIAL_ERRORS = {
  name: "",
  email: "",
  phone: "",
  profile: "",
  form: "",
};

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL?.replace(/\/$/, "") ||
  (window.location.hostname === "localhost" ? "http://localhost:8000/api" : "/api");
const DEFAULT_JOBS_SINK_URL =
  "https://script.google.com/macros/s/AKfycbzmYaBQak7kAGDsjqhV2LTesJqzBTzpXsx0NIv68ipOKUSz4xyCxj__bz6IWGwlM3WXJQ/exec";
const SECONDARY_SINK_URL = process.env.REACT_APP_JOBS_SINK_URL?.trim() || DEFAULT_JOBS_SINK_URL;

function updateMetaTag(name, content, attribute = "name") {
  const selector = attribute === "name" ? `meta[name="${name}"]` : `meta[property="${name}"]`;
  let element = document.head.querySelector(selector);

  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, name);
    document.head.appendChild(element);
  }

  const previous = element.getAttribute("content");
  element.setAttribute("content", content);

  return () => {
    if (previous === null) {
      element.remove();
      return;
    }
    element.setAttribute("content", previous);
  };
}

function updateCanonical(href) {
  let element = document.head.querySelector('link[rel="canonical"]');

  if (!element) {
    element = document.createElement("link");
    element.setAttribute("rel", "canonical");
    document.head.appendChild(element);
  }

  const previous = element.getAttribute("href");
  element.setAttribute("href", href);

  return () => {
    if (previous === null) {
      element.remove();
      return;
    }
    element.setAttribute("href", previous);
  };
}

function normalizeWhitespace(value) {
  return value.trim().replace(/\s+/g, " ");
}

function validatePrimaryForm(form) {
  const errors = { ...INITIAL_ERRORS };

  if (normalizeWhitespace(form.name).length < 2) {
    errors.name = "Enter your full name.";
  }

  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
    errors.email = "Enter a valid email.";
  }

  if (form.phone.trim()) {
    const digits = form.phone.replace(/[^\d]/g, "");
    if (digits.length < 7) {
      errors.phone = "Enter a valid phone number.";
    }
  }

  return errors;
}

function buildApiUrl(path) {
  return `${API_BASE_URL}${path}`;
}

async function requestJson(path, options) {
  const response = await fetch(buildApiUrl(path), {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers ?? {}),
    },
  });

  const contentType = response.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");
  const data = isJson ? await response.json().catch(() => null) : null;

  if (!response.ok) {
    let message = data?.detail || data?.error || "Request failed";

    if (!isJson && (response.status === 404 || response.status === 405)) {
      message = "Submission service is not live yet. Please email founders@lab0.ai for now.";
    }

    const error = new Error(message);
    error.fields = data?.fields || {};
    throw error;
  }

  if (!isJson || !data) {
    throw new Error("Submission service is not live yet. Please email founders@lab0.ai for now.");
  }

  return data;
}

async function sendSecondarySink(eventType, submission) {
  if (!SECONDARY_SINK_URL) {
    return;
  }

  try {
    await fetch(SECONDARY_SINK_URL, {
      method: "POST",
      mode: "no-cors",
      headers: {
        "Content-Type": "text/plain;charset=utf-8",
      },
      body: JSON.stringify({
        event_type: eventType,
        source: "startup-school",
        submitted_from: "lab0.ai/jobs",
        submission,
      }),
    });
  } catch (error) {
    console.error("Secondary jobs sink failed", error);
  }
}

function trackEvent(name, properties = {}) {
  if (window.posthog?.capture) {
    window.posthog.capture(name, properties);
  }
}

function buildClientSubmission(payload, overrides = {}) {
  const submissionId = overrides.id || window.crypto?.randomUUID?.() || `jobs-${Date.now()}`;

  return {
    id: submissionId,
    name: payload.name,
    email: payload.email,
    profile: payload.profile,
    phone: payload.phone,
    role: payload.role,
    hard_problems: payload.hard_problems,
    created_at: overrides.created_at || new Date().toISOString(),
    github_added_at: overrides.github_added_at || null,
  };
}

function submitViaPosthog(payload, existingSubmission = null) {
  if (!window.posthog?.capture) {
    throw new Error("Submission service is unavailable right now. Please try again in a moment.");
  }

  const submission = buildClientSubmission(payload, existingSubmission || {});

  if (window.posthog.identify) {
    window.posthog.identify(payload.email.trim().toLowerCase(), {
      name: payload.name,
      email: payload.email.trim().toLowerCase(),
      jobs_submission_id: submission.id,
      jobs_profile: payload.profile || null,
      jobs_phone: payload.phone || null,
      jobs_role: payload.role || null,
      jobs_hard_problems: payload.hard_problems || null,
      jobs_source: "startup-school",
      jobs_submitted_at: submission.created_at,
    });
  }

  window.posthog.capture("jobs_interest_submitted", {
    submission_id: submission.id,
    name: payload.name,
    email: payload.email.trim().toLowerCase(),
    profile: payload.profile || null,
    phone: payload.phone || null,
    role: payload.role || null,
    source: "startup-school",
    capture_method: "posthog_direct",
  });

  return submission;
}

function updateGithubViaPosthog(submission, githubValue) {
  if (!window.posthog?.capture) {
    throw new Error("GitHub update service is unavailable right now. Please try again in a moment.");
  }

  const updatedSubmission = {
    ...submission,
    profile: githubValue,
    github_added_at: new Date().toISOString(),
  };

  if (window.posthog.identify && submission.email) {
    window.posthog.identify(submission.email.trim().toLowerCase(), {
      jobs_submission_id: submission.id,
      jobs_profile: githubValue,
      jobs_github_added_at: updatedSubmission.github_added_at,
    });
  }

  window.posthog.capture("jobs_github_added", {
    submission_id: submission.id,
    email: submission.email?.trim().toLowerCase() || null,
    github_profile: githubValue,
    capture_method: "posthog_direct",
  });

  return updatedSubmission;
}

function JobsModal({
  isOpen,
  onClose,
  form,
  onFormChange,
  onSubmit,
  isSubmitting,
  errors,
  successSubmission,
  githubValue,
  onGithubValueChange,
  onGithubSubmit,
  isSavingGithub,
  githubError,
  isGithubFormOpen,
  onToggleGithubForm,
}) {
  const firstInputRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const isDesktop = window.matchMedia("(min-width: 768px)").matches;
    if (isDesktop) {
      window.requestAnimationFrame(() => {
        firstInputRef.current?.focus();
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const scrollY = window.scrollY;
    document.body.style.position = "fixed";
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = "0";
    document.body.style.right = "0";
    document.body.style.width = "100%";

    return () => {
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      window.scrollTo(0, scrollY);
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-center md:items-center md:p-6">
      <button
        type="button"
        aria-label="Close form"
        className="absolute inset-0 bg-black/35 backdrop-blur-md"
        onClick={onClose}
      />

      <section className="relative z-10 flex min-h-screen w-full flex-col overflow-y-auto border border-black/[0.08] bg-white px-4 pb-7 pt-5 text-[#1a1a1a] shadow-2xl shadow-black/[0.18] md:min-h-0 md:max-h-[88vh] md:max-w-xl md:rounded-[8px] md:px-6 md:pb-6">
        <button
          type="button"
          aria-label="Close form"
          className="absolute right-4 top-4 inline-flex h-10 w-10 items-center justify-center border border-black/[0.08] bg-white text-[#1a1a1a]/70 transition-colors hover:bg-emerald-700/[0.04]"
          onClick={onClose}
        >
          <X className="h-4 w-4" />
        </button>

        {!successSubmission ? (
          <>
            <div className="pr-12">
              <h2 className="mt-3 font-display text-3xl font-semibold text-[#1a1a1a]">
                Drop your details
              </h2>
              <p className="mt-3 max-w-sm text-sm leading-6 text-[#1a1a1a]/50">
                Takes 15 sec. Name and email are compulsory. Everything else is optional.
              </p>
            </div>

            <form className="mt-6 space-y-4" onSubmit={onSubmit} noValidate>
              <div>
                <label className="mb-2 block text-sm text-[#1a1a1a]/70" htmlFor="jobs-name">
                  Name
                </label>
                <input
                  ref={firstInputRef}
                  id="jobs-name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  placeholder="Alan Turing"
                  value={form.name}
                  onChange={onFormChange}
                  className="h-14 w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20"
                  required
                />
                <p className="mt-2 min-h-5 text-sm text-red-600">{errors.name}</p>
              </div>

              <div>
                <label className="mb-2 block text-sm text-[#1a1a1a]/70" htmlFor="jobs-email">
                  Email
                </label>
                <input
                  id="jobs-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  inputMode="email"
                  placeholder="alan@stanford.edu"
                  value={form.email}
                  onChange={onFormChange}
                  className="h-14 w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20"
                  required
                />
                <p className="mt-2 min-h-5 text-sm text-red-600">{errors.email}</p>
              </div>

              <div>
                <label className="mb-2 block text-sm text-[#1a1a1a]/70" htmlFor="jobs-profile">
                  LinkedIn / X / GitHub
                </label>
                <input
                  id="jobs-profile"
                  name="profile"
                  type="text"
                  autoComplete="off"
                  placeholder="@alan"
                  value={form.profile}
                  onChange={onFormChange}
                  className="h-14 w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20"
                />
                <p className="mt-2 min-h-5 text-sm text-red-600">{errors.profile}</p>
              </div>

              <div>
                <label className="mb-2 block text-sm text-[#1a1a1a]/70" htmlFor="jobs-phone">
                  Phone
                </label>
                <input
                  id="jobs-phone"
                  name="phone"
                  type="tel"
                  autoComplete="tel"
                  inputMode="tel"
                  placeholder="+1 415 555 0142"
                  value={form.phone}
                  onChange={onFormChange}
                  className="h-14 w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20"
                />
                <p className="mt-2 min-h-5 text-sm text-red-600">{errors.phone}</p>
              </div>

              <fieldset>
                <legend className="mb-2 text-sm text-[#1a1a1a]/70">
                  Role
                </legend>
                <select
                  name="role"
                  value={form.role}
                  onChange={onFormChange}
                  className="h-14 w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20"
                >
                  <option value="">Select role</option>
                  <option value="Student">Student</option>
                  <option value="New grad">New grad</option>
                  <option value="Experienced engineer">Experienced engineer</option>
                </select>
              </fieldset>

              <div>
                <label className="mb-2 block text-sm text-[#1a1a1a]/70" htmlFor="jobs-hard-problems">
                  What are some of the most difficult problems you've worked on and how did you solve them?
                </label>
                <textarea
                  id="jobs-hard-problems"
                  name="hard_problems"
                  rows={4}
                  placeholder="Share a technical or non-technical challenge and your approach..."
                  value={form.hard_problems}
                  onChange={onFormChange}
                  className="w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 py-3 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20 resize-none"
                />
              </div>

              <div className="pt-2">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="inline-flex h-14 w-full items-center justify-center gap-2 rounded-[8px] bg-[#077757] px-5 text-base font-medium text-white transition hover:bg-[#066349] disabled:cursor-wait disabled:opacity-70"
                >
                  {isSubmitting ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                  {isSubmitting ? "Sending..." : "Send"}
                </button>
                <p className="mt-3 text-center text-sm text-[#1a1a1a]/40">No resume. No ATS. Just enough to follow up.</p>
                <p className="mt-2 min-h-5 text-center text-sm text-red-600">{errors.form}</p>
              </div>
            </form>
          </>
        ) : (
          <>
            <div className="pr-12">
              <h2 className="mt-3 font-display text-3xl font-semibold text-[#1a1a1a]">
                Done — we&apos;ll follow up soon.
              </h2>
              <p className="mt-3 text-sm leading-6 text-[#1a1a1a]/50">Want to fast-track?</p>
            </div>

            <div className="mt-6 grid gap-3">
              <button
                type="button"
                className="inline-flex min-h-12 items-center justify-center rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-sm text-[#1a1a1a]/75 transition hover:bg-emerald-700/[0.04]"
                onClick={onToggleGithubForm}
              >
                Add GitHub
              </button>
              <a
                className="inline-flex min-h-12 items-center justify-center rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-sm text-[#1a1a1a]/75 transition hover:bg-emerald-700/[0.04]"
                href={siteConfig.demoUrl}
                target="_blank"
                rel="noreferrer"
              >
                Book a demo
              </a>
              <a
                className="inline-flex min-h-12 items-center justify-center rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-sm text-[#1a1a1a]/75 transition hover:bg-emerald-700/[0.04]"
                href="https://lab0.ai"
                target="_blank"
                rel="noreferrer"
              >
                Read more about Lab0
              </a>
            </div>

            {isGithubFormOpen ? (
              <form className="mt-5 border-t border-black/[0.08] pt-5" onSubmit={onGithubSubmit} noValidate>
                <label className="mb-2 block text-sm text-[#1a1a1a]/70" htmlFor="jobs-github">
                  GitHub username or URL
                </label>
                <div className="grid gap-3 md:grid-cols-[1fr_auto]">
                  <input
                    id="jobs-github"
                    name="github"
                    type="text"
                    autoComplete="off"
                    placeholder="alan or github.com/alan"
                    value={githubValue}
                    onChange={onGithubValueChange}
                    className="h-14 w-full rounded-[8px] border border-black/[0.1] bg-[#fafaf9] px-4 text-base text-[#1a1a1a] outline-none transition focus:border-[#077757] focus:ring-2 focus:ring-[#077757]/20"
                  />
                  <button
                    type="submit"
                    disabled={isSavingGithub}
                    className="inline-flex h-14 items-center justify-center gap-2 rounded-[8px] bg-[#077757] px-5 text-base font-medium text-white transition hover:bg-[#066349] disabled:cursor-wait disabled:opacity-70"
                  >
                    {isSavingGithub ? <LoaderCircle className="h-4 w-4 animate-spin" /> : null}
                    {isSavingGithub ? "Saving..." : "Save"}
                  </button>
                </div>
                <p className="mt-2 min-h-5 text-sm text-red-600">{githubError}</p>
                <p className="mt-1 text-sm text-[#1a1a1a]/40">Useful if you originally sent LinkedIn, X, or nothing.</p>
              </form>
            ) : null}
          </>
        )}
      </section>
    </div>
  );
}

export default function JobsPage() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [form, setForm] = useState(INITIAL_FORM);
  const [errors, setErrors] = useState(INITIAL_ERRORS);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submission, setSubmission] = useState(null);
  const [isGithubFormOpen, setIsGithubFormOpen] = useState(false);
  const [githubValue, setGithubValue] = useState("");
  const [githubError, setGithubError] = useState("");
  const [isSavingGithub, setIsSavingGithub] = useState(false);

  useEffect(() => {
    const restoreTitle = document.title;
    document.title = "Lab0 Jobs | Build AI that deploys enterprise software";

    const restoreDescription = updateMetaTag(
      "description",
      "Met Lab0 at Startup School? Drop your details in 15 seconds and fast-track interest for engineering roles building AI agents for enterprise software deployment.",
    );
    const restoreTheme = updateMetaTag("theme-color", "#fafaf9");
    const restoreOgTitle = updateMetaTag(
      "og:title",
      "Lab0 Jobs | Build AI that deploys enterprise software",
      "property",
    );
    const restoreOgDescription = updateMetaTag(
      "og:description",
      "Lab0 (YC P26) is hiring strong engineers and builders. Fast interest capture for Startup School attendees.",
      "property",
    );
    const restoreCanonical = updateCanonical("https://lab0.ai/jobs");

    return () => {
      document.title = restoreTitle;
      restoreDescription();
      restoreTheme();
      restoreOgTitle();
      restoreOgDescription();
      restoreCanonical();
    };
  }, []);

  const handleOpen = () => {
    setIsModalOpen(true);
    trackEvent("jobs_form_opened");
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: "", form: "" }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const nextErrors = validatePrimaryForm(form);

    if (Object.values(nextErrors).some(Boolean)) {
      setErrors(nextErrors);
      return;
    }

    setIsSubmitting(true);
    setErrors(INITIAL_ERRORS);

    try {
      const payload = {
        name: normalizeWhitespace(form.name),
        email: form.email.trim(),
        profile: normalizeWhitespace(form.profile) || null,
        phone: normalizeWhitespace(form.phone) || null,
        role: form.role || null,
        hard_problems: normalizeWhitespace(form.hard_problems) || null,
      };

      let createdSubmission;

      try {
        createdSubmission = await requestJson("/jobs", {
          method: "POST",
          body: JSON.stringify(payload),
        });
      } catch (error) {
        createdSubmission = submitViaPosthog(payload);
      }

      setSubmission(createdSubmission);
      void sendSecondarySink("jobs_interest_submitted", createdSubmission);
      trackEvent("jobs_submission_created", { role: payload.role || "unknown" });
    } catch (error) {
      setErrors((current) => ({
        ...current,
        ...error.fields,
        form: error.message || "Unable to send right now. Try again in a moment.",
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGithubSubmit = async (event) => {
    event.preventDefault();
    setGithubError("");

    const value = normalizeWhitespace(githubValue);
    if (value.length < 2) {
      setGithubError("Add a GitHub username or URL.");
      return;
    }

    if (!submission?.id) {
      setGithubError("Original submission missing. Reload and try again.");
      return;
    }

    setIsSavingGithub(true);

    try {
      let updatedSubmission;

      try {
        updatedSubmission = await requestJson(`/jobs/${submission.id}`, {
          method: "PATCH",
          body: JSON.stringify({ profile: value }),
        });
      } catch (error) {
        updatedSubmission = updateGithubViaPosthog(submission, value);
      }

      setSubmission(updatedSubmission);
      void sendSecondarySink("jobs_github_added", updatedSubmission);
      setGithubValue("");
      setIsGithubFormOpen(false);
      trackEvent("jobs_github_added");
    } catch (error) {
      setGithubError(error.fields?.profile || error.message || "Unable to save GitHub right now.");
    } finally {
      setIsSavingGithub(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#fafaf9] text-[#1a1a1a]">
      <div className="grain-overlay" aria-hidden="true" />
      <Navbar />

      <main>
        <section className="jobs-hero">
          <div className="absolute inset-0 overflow-hidden pointer-events-none">
            <div className="absolute top-0 left-1/4 w-px h-full bg-black/[0.04]" />
            <div className="absolute top-0 left-2/4 w-px h-full bg-black/[0.04]" />
            <div className="absolute top-0 left-3/4 w-px h-full bg-black/[0.04]" />
            <div className="absolute top-1/4 left-0 w-full h-px bg-black/[0.04]" />
            <div className="absolute top-2/4 left-0 w-full h-px bg-black/[0.04]" />
            <div className="absolute top-3/4 left-0 w-full h-px bg-black/[0.04]" />
          </div>

          <div className="jobs-hero-inner">
            <div className="jobs-hero-copy">
              <h1>
                <span>build the agent</span>
                <span>that deploys software</span>
              </h1>
              <p>
                Lab0 is hiring engineers and builders to turn enterprise implementation from a services-heavy rollout into a product-grade agent workflow.
              </p>
              <button
                type="button"
                onClick={handleOpen}
                className="jobs-hero-cta group"
              >
                Drop your details
                <ArrowUpRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </button>
            </div>

            <div className="jobs-pipeline-shell">
              <div className="jobs-pipeline-topbar">
                <Terminal className="w-4 h-4 text-emerald-700/70" />
                <div className="flex gap-1" aria-hidden="true">
                  <span className="h-2 w-2 rounded-full bg-black/[0.08]" />
                  <span className="h-2 w-2 rounded-full bg-black/[0.08]" />
                  <span className="h-2 w-2 rounded-full bg-black/[0.08]" />
                </div>
              </div>
              <div className="jobs-pipeline-body">
                {[
                  "read application",
                  "capture contact details",
                  "route to founder review",
                  "follow up with the right next step",
                ].map((line, index) => (
                  <div key={line} className="jobs-pipeline-row">
                    <span>{String(index + 1).padStart(2, "0")}</span>
                    <strong>{line}</strong>
                  </div>
                ))}
                <div className="jobs-pipeline-result">
                  <span>✓</span>
                  <strong>details captured for founder review</strong>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="relative px-6 lg:px-8 py-24 lg:py-32">
          <div className="absolute top-0 left-6 right-6 lg:left-8 lg:right-8 h-px bg-black/[0.06]" />
          <div className="mx-auto grid w-full max-w-7xl gap-px bg-black/[0.06] border border-black/[0.06] md:grid-cols-3">
            {[
              ["Own hard product surfaces", "Build the interfaces, workflows, and agent behaviors that make enterprise deployment feel self-serve."],
              ["Work close to customers", "The product is shaped by real implementation calls, messy data, security blockers, and production rollouts."],
              ["Ship with founder-level pace", "Small team, high ownership, direct feedback loops, and no layers between the problem and the builder."],
            ].map(([title, description], index) => (
              <article key={title} className="bg-white p-8 lg:p-10">
                <div className="font-mono text-xs text-[#1a1a1a]/15 mb-4">
                  {String(index + 1).padStart(2, "0")}
                </div>
                <h2 className="font-display text-xl lg:text-2xl font-semibold text-[#1a1a1a]/85 mb-3">
                  {title}
                </h2>
                <p className="text-sm text-[#1a1a1a]/45 leading-relaxed">{description}</p>
              </article>
            ))}
          </div>
        </section>
      </main>

      <Footer />

      <JobsModal
        isOpen={isModalOpen}
        onClose={handleClose}
        form={form}
        onFormChange={handleFormChange}
        onSubmit={handleSubmit}
        isSubmitting={isSubmitting}
        errors={errors}
        successSubmission={submission}
        githubValue={githubValue}
        onGithubValueChange={(event) => {
          setGithubValue(event.target.value);
          setGithubError("");
        }}
        onGithubSubmit={handleGithubSubmit}
        isSavingGithub={isSavingGithub}
        githubError={githubError}
        isGithubFormOpen={isGithubFormOpen}
        onToggleGithubForm={() => {
          setIsGithubFormOpen((current) => !current);
          setGithubError("");
        }}
      />

      <div className="sr-only" aria-live="polite">
        {submission ? "Details sent successfully" : ""}
      </div>

      {submission ? (
        <div className="pointer-events-none fixed bottom-4 left-1/2 z-40 hidden -translate-x-1/2 border border-black/[0.08] bg-white px-4 py-2 text-sm text-[#1a1a1a]/70 shadow-lg shadow-black/[0.08] md:flex">
          <Check className="mr-2 h-4 w-4 text-[#0ca775]" />
          Interest captured
        </div>
      ) : null}
    </div>
  );
}
